import { Component, Input, Output, EventEmitter, inject, Injector, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LocalizationService } from '@abp/ng.core';
import { Subject } from 'rxjs';
import { FieldDefinition } from '../fields/field-definition';
import { createFkDisplayResolver, FkDisplayResolver } from '../fields/fk/fk-display-resolver.helper';
import { localizeWithFallback } from '../utils/localization-utils';
import { ZendraLocalizationPipe } from '../pipes/zendra-localization.pipe';
import { parseIsoDate } from '../generic-table/filters/utils/date-value';
import { parseDateOnlyFieldValue } from '../utils/date-only-value';

@Component({
  selector: 'zn-hierarchy-navigator',
  imports: [CommonModule, ZendraLocalizationPipe],
  templateUrl: './zn-hierarchy-navigator.html',
  styleUrl: './zn-hierarchy-navigator.scss'
})
export class ZnHierarchyNavigator implements OnDestroy {
  @Input() parentTitle: string = '';
  @Input() parentSummaryData: any = null;
  @Input() parentSummaryFields: FieldDefinition[] | null = null;
  @Input() isExpanded: boolean = false;
  @Input() hierarchyLevel?: number;
  @Input() showFrame: boolean = true;
  @Output() accordionToggled = new EventEmitter<boolean>();
  @Output() accordionTitleClick = new EventEmitter<void>();

  private readonly localizationService = inject(LocalizationService);
  private readonly injector = inject(Injector);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();
  private readonly fkResolvers = new Map<string, FkDisplayResolver>();

  toggleAccordion(): void {
    this.isExpanded = !this.isExpanded;
    this.accordionToggled.emit(this.isExpanded);
  }

  onTitleClick(): void {
    this.accordionTitleClick.emit();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getSummaryItems(): Array<{ label: string; value: any }> {
    if (!this.parentSummaryData) {
      return [];
    }

    const items: Array<{ label: string; value: any }> = [];

    if (this.parentSummaryFields?.length) {
      this.parentSummaryFields.forEach(field => {
        const key = field.key;
        const baseKey = key.toLowerCase().endsWith('id') ? key.slice(0, -2) : null;
        const summaryPath =
          field.column.params?.summaryField || field.column.params?.displayNestedPath;

        let rawValue =
          this.parentSummaryData[key] ??
          (baseKey ? this.parentSummaryData[baseKey] : undefined);

        if (summaryPath) {
          const resolved = summaryPath
            .split('.')
            .reduce((current: any, part: string) => current?.[part], this.parentSummaryData);
          if (resolved !== null && resolved !== undefined) {
            rawValue = resolved;
          }
        }

        if (this.shouldSkipField(key, rawValue, field)) {
          return;
        }

        const label = field.label || this.formatLabel(key);
        const formattedValue = this.formatValue(rawValue, field);

        items.push({ label, value: formattedValue });
      });
      return items;
    }

    Object.keys(this.parentSummaryData).forEach(key => {
      if (this.shouldSkipField(key, this.parentSummaryData[key])) {
        return;
      }

      const value = this.parentSummaryData[key];
      const label = this.formatLabel(key);
      const formattedValue = this.formatValue(value);

      items.push({ label, value: formattedValue });
    });

    return items;
  }

  private shouldSkipField(key: string, value?: any, field?: FieldDefinition): boolean {
    const lowerKey = key.toLowerCase();

    if (lowerKey.endsWith('id')) {
      if (value === null || value === undefined) {
        return true;
      }
      if (typeof value === 'number') {
        return !field?.foreignKey;
      }
      return false;
    }

    const auditFields = [
      '_chevron',
      'creationtime',
      'creatorid',
      'lastmodificationtime',
      'lastmodifierid',
      'deletiontime',
      'deleterid',
      'isdeleted',
      'concurrencystamp'
    ];

    if (auditFields.includes(lowerKey)) {
      return true;
    }

    return false;
  }

  private formatLabel(key: string): string {
    const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);

    if (this.parentTitle) {
      return `${this.parentTitle}.${capitalizedKey}`;
    }

    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }

  private formatValue(value: any, field?: FieldDefinition): string {
    if (value === null || value === undefined) {
      return '-';
    }

    if (typeof value === 'boolean') {
      return value
        ? localizeWithFallback(this.localizationService, 'AbpUi::Yes')
        : localizeWithFallback(this.localizationService, 'AbpUi::No');
    }

    const formattedDate = this.formatDateValue(value, field);
    if (formattedDate !== null) {
      return formattedDate;
    }

    if (field?.foreignKey && (typeof value === 'number' || typeof value === 'string')) {
      const resolver = this.getOrCreateResolver(field);
      const resolved = resolver.resolve(value);
      return resolved ?? '...';
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
      const displayField = field?.foreignKey?.displayField;
      return value?.[displayField] ?? '-';
    }

    return String(value);
  }

  private formatDateValue(value: any, field?: FieldDefinition): string | null {
    if (field?.fieldOptions?.controlType !== 'datebox') {
      return null;
    }

    const locale = this.localizationService.currentLang || 'es-ES';
    const inputType = field.fieldOptions?.type;

    if (inputType === 'date' && typeof value === 'string') {
      const dateOnlyValue = parseDateOnlyFieldValue(value);
      if (dateOnlyValue) {
        return dateOnlyValue.toLocaleDateString(locale);
      }
    }

    const dateValue = value instanceof Date ? value : parseIsoDate(String(value));
    if (!dateValue || Number.isNaN(dateValue.getTime())) {
      return null;
    }

    return inputType === 'datetime-local'
      ? dateValue.toLocaleString(locale)
      : dateValue.toLocaleDateString(locale);
  }

  private getOrCreateResolver(field: FieldDefinition): FkDisplayResolver {
    if (!this.fkResolvers.has(field.key)) {
      this.fkResolvers.set(
        field.key,
        createFkDisplayResolver(
          this.injector,
          field.foreignKey!,
          { onResolved: () => this.changeDetector.detectChanges() },
          this.destroy$
        )
      );
    }
    return this.fkResolvers.get(field.key)!;
  }
}
